import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { listingsAPI, favoritesAPI, formatApiError } from '../lib/api';
import { Calendar, Gauge, Fuel, Zap, Settings2, Palette, MapPin, Eye, Star, ArrowLeft, CreditCard, ChevronLeft, ChevronRight, Lock, Shield, Heart, Mail, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

export default function ListingDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImg, setCurrentImg] = useState(0);
  const [promoting, setPromoting] = useState(false);
  const [isFav, setIsFav] = useState(false);

  useEffect(() => {
    listingsAPI.getOne(id).then(r => setListing(r.data)).catch(() => navigate('/listings')).finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => {
    if (user && listing) {
      favoritesAPI.getAll().then(r => {
        setIsFav(r.data.favorite_ids?.includes(listing.id));
      }).catch(() => {});
    }
  }, [user, listing]);

  const toggleFavorite = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      if (isFav) {
        await favoritesAPI.remove(listing.id);
        setIsFav(false);
        toast.success('Removed from favorites');
      } else {
        await favoritesAPI.add(listing.id);
        setIsFav(true);
        toast.success('Added to favorites');
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handlePromote = async () => {
    if (!user) { navigate('/login'); return; }
    setPromoting(true);
    try {
      const origin = window.location.origin;
      const { data } = await listingsAPI.promote(listing.id, origin);
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPromoting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('listing.deleteConfirm'))) return;
    try {
      await listingsAPI.delete(listing.id);
      toast.success('Deleted');
      navigate('/dashboard');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen bg-[#0B0F19]"><div className="w-8 h-8 border-2 border-[#FADB5F] border-t-transparent rounded-full animate-spin" /></div>;
  if (!listing) return null;

  const isFeatured = listing.is_featured && listing.featured_until && new Date(listing.featured_until) > new Date();
  const isOwner = user && user._id === listing.user_id;
  const images = listing.images?.length > 0 ? listing.images : [];
  const hasImages = images.length > 0;

  const specs = [
    { icon: Calendar, label: t('listing.year'), value: listing.year },
    listing.mileage != null && { icon: Gauge, label: t('listing.mileage'), value: `${listing.mileage?.toLocaleString()} ${t('listing.km')}` },
    listing.fuel_type && { icon: Fuel, label: t('listing.fuel'), value: t(`fuel_types.${listing.fuel_type}`) || listing.fuel_type },
    listing.power_hp && { icon: Zap, label: t('listing.power'), value: `${listing.power_hp} ${t('listing.hp')}` },
    listing.transmission && { icon: Settings2, label: t('listing.transmission'), value: t(`transmission_types.${listing.transmission}`) || listing.transmission },
    listing.color && { icon: Palette, label: t('listing.color'), value: listing.color },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#0B0F19]" data-testid="listing-detail-page">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#94A3B8] hover:text-[#FADB5F] mb-4 transition-colors" data-testid="back-button">
          <ArrowLeft className="w-4 h-4" /> {t('common.back')}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="relative rounded-xl overflow-hidden bg-[#141B2D] border border-[#1E293B]" data-testid="image-gallery">
              <div className="aspect-[16/10] relative">
                {hasImages ? (
                  <img src={images[currentImg]} alt={listing.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-[#1E293B]">
                    <div className="text-center">
                      <Calendar className="w-12 h-12 text-[#475569] mx-auto mb-2" />
                      <p className="text-sm text-[#475569]">No photo</p>
                    </div>
                  </div>
                )}
                {isFeatured && (
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-[#FADB5F]/90 text-[#856505] font-bold px-3 py-1 tracking-wider border-0">
                      <Star className="w-3.5 h-3.5 mr-1 fill-[#856505]" /> {t('listing.featured')}
                    </Badge>
                  </div>
                )}
                {images.length > 1 && (
                  <>
                    <button onClick={() => setCurrentImg(i => i > 0 ? i - 1 : images.length - 1)} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#0B0F19]/80 backdrop-blur flex items-center justify-center hover:bg-[#0B0F19] transition" data-testid="prev-image">
                      <ChevronLeft className="w-5 h-5 text-[#F1F5F9]" />
                    </button>
                    <button onClick={() => setCurrentImg(i => i < images.length - 1 ? i + 1 : 0)} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#0B0F19]/80 backdrop-blur flex items-center justify-center hover:bg-[#0B0F19] transition" data-testid="next-image">
                      <ChevronRight className="w-5 h-5 text-[#F1F5F9]" />
                    </button>
                  </>
                )}
              </div>
              {images.length > 1 && (
                <div className="flex gap-2 p-3 overflow-x-auto">
                  {images.map((img, i) => (
                    <button key={i} onClick={() => setCurrentImg(i)} className={`w-16 h-12 rounded-md overflow-hidden border-2 transition shrink-0 ${i === currentImg ? 'border-[#FADB5F]' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6" data-testid="listing-details">
              <h2 className="text-lg font-semibold text-[#F1F5F9] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('listing.details')}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {specs.map((spec, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[#0B0F19]">
                    <spec.icon className="w-5 h-5 text-[#FADB5F] mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-[#94A3B8]">{spec.label}</p>
                      <p className="text-sm font-medium text-[#F1F5F9]">{spec.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6" data-testid="listing-description">
              <h2 className="text-lg font-semibold text-[#F1F5F9] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('listing.description')}</h2>
              <p className="text-sm text-[#94A3B8] leading-relaxed whitespace-pre-wrap" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {listing.description}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className={`bg-[#141B2D] rounded-xl border p-6 ${isFeatured ? 'border-[#FADB5F]/50 ring-1 ring-[#FADB5F]/20' : 'border-[#1E293B]'}`} data-testid="price-card">
              <div className="flex items-start justify-between">
                <h1 className="text-2xl font-bold text-[#FADB5F] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {listing.price?.toLocaleString()} {listing.currency || 'EUR'}
                </h1>
                <button onClick={toggleFavorite} className={`p-2 rounded-lg transition-colors ${isFav ? 'bg-red-500/10 text-red-400' : 'bg-[#1E293B] text-[#475569] hover:text-red-400'}`} data-testid="favorite-btn">
                  <Heart className={`w-5 h-5 ${isFav ? 'fill-red-400' : ''}`} />
                </button>
              </div>
              <h2 className="text-lg font-semibold text-[#F1F5F9] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {listing.title}
              </h2>
              <div className="flex items-center gap-2 text-xs text-[#94A3B8] mb-1">
                <MapPin className="w-3.5 h-3.5" /> {listing.city}, {listing.country}
              </div>
              <div className="flex items-center gap-2 text-xs text-[#94A3B8] mb-1">
                <Eye className="w-3.5 h-3.5" /> {listing.views} {t('listing.views')}
              </div>
              <p className="text-xs text-[#94A3B8]">{listing.brand} {listing.model} &middot; {listing.year}</p>
            </div>

            <div className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6" data-testid="seller-card">
              <h3 className="text-sm font-semibold text-[#F1F5F9] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('listing.seller')}</h3>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#0A3A82] flex items-center justify-center">
                  <span className="text-sm font-bold text-white">{listing.user_name?.[0]?.toUpperCase()}</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-[#F1F5F9]">{listing.user_name}</p>
                  <Link to={`/profile/${listing.user_id}`} className="text-xs text-[#FADB5F] hover:underline">{t('common.viewAll')}</Link>
                </div>
              </div>
              {/* Contact info */}
              <div className="space-y-2 p-3 rounded-lg bg-[#0B0F19] border border-[#1E293B]">
                {listing.user_email && (
                  <div className="flex items-center gap-2 text-xs">
                    <Mail className="w-3.5 h-3.5 text-[#FADB5F]" />
                    <a href={`mailto:${listing.user_email}`} className="text-[#94A3B8] hover:text-[#FADB5F] transition-colors">{listing.user_email}</a>
                  </div>
                )}
                {listing.user_phone && (
                  <div className="flex items-center gap-2 text-xs">
                    <Phone className="w-3.5 h-3.5 text-[#FADB5F]" />
                    <a href={`tel:${listing.user_phone}`} className="text-[#94A3B8] hover:text-[#FADB5F] transition-colors">{listing.user_phone}</a>
                  </div>
                )}
              </div>
            </div>

            {isOwner && (
              <div className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6 space-y-3" data-testid="owner-actions">
                {!isFeatured && (
                  <div className="bg-[#FADB5F]/10 rounded-lg p-4 mb-3 border border-[#FADB5F]/20">
                    <h4 className="text-sm font-semibold text-[#FADB5F] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('payment.promoteTitle')}</h4>
                    <p className="text-xs text-[#94A3B8] mb-2">{t('payment.promoteDesc')}</p>
                    <p className="text-lg font-bold text-[#FADB5F]">{t('payment.price')}</p>
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#FADB5F]/10">
                      <div className="flex items-center gap-1">
                        <Lock className="w-3 h-3 text-green-400" />
                        <span className="text-[10px] text-[#94A3B8]">SSL</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Shield className="w-3 h-3 text-[#FADB5F]" />
                        <span className="text-[10px] text-[#94A3B8]">PCI DSS</span>
                      </div>
                      <span className="text-[10px] text-[#94A3B8]">Powered by Stripe</span>
                    </div>
                  </div>
                )}
                {!isFeatured && (
                  <Button onClick={handlePromote} disabled={promoting} className="w-full bg-[#FADB5F] text-[#0F172A] hover:bg-[#E5C34A] font-semibold" data-testid="promote-btn">
                    <CreditCard className="w-4 h-4 mr-2" /> {t('payment.promoteBtn')}
                  </Button>
                )}
                <Button variant="outline" onClick={() => navigate(`/edit-listing/${listing.id}`)} className="w-full border-[#1E293B] text-[#94A3B8] hover:bg-[#1E293B]" data-testid="edit-listing-btn">
                  {t('listing.edit')}
                </Button>
                <Button variant="outline" onClick={handleDelete} className="w-full text-red-400 hover:bg-red-950/30 border-red-900/50" data-testid="delete-listing-btn">
                  {t('listing.delete')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
